import React, { useState, useEffect } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Paper,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import {
  getProveedorTipos,
  updateProveedor,
  createProveedor,
} from "../../api/ProveedoresyFabricas/proveedoresApi";

const ACCENT = "#90b2b7";
const ACCENT_HOVER = "#7a9ea3";

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 2 },
};

const ProveedoresFichaDrawer = ({ proveedor, open, onClose, onSave }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [tiposProveedores, setTiposProveedores] = useState([]);

  const isNew = !proveedor;

  useEffect(() => {
    const fetchTipos = async () => {
      try {
        const tipos = await getProveedorTipos();
        setTiposProveedores(tipos);
      } catch (error) {
        console.error("Error al obtener tipos de proveedores:", error);
      }
    };

    fetchTipos();
  }, []);

  useEffect(() => {
    if (proveedor) {
      setFormData({
        ...proveedor,
        ProveedorTipo: proveedor.tipoProveedor?.id_ProveedorTipo || "",
      });
    } else {
      setFormData({
        N_PROVEEDOR: "",
        DIRECCION: "",
        NVIA: "",
        CP: "",
        POBLACION: "",
        PROVINCIA: "",
        CODPAIS: "",
        PAIS: "",
        EMAIL: "",
        TELEFONO: "",
        MONEDA: "",
        ProveedorTipo: "",
      });
    }
  }, [proveedor]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "ProveedorTipo") {
      setFormData((prevData) => ({
        ...prevData,
        ProveedorTipo: value,
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const handleSave = async () => {
    try {
      if (isNew) {
        const created = await createProveedor(formData);
        onSave(created);
      } else {
        const updated = await updateProveedor(formData);
        onSave(updated);
      }
      onClose();
    } catch (e) {
      console.error("Error guardando proveedor:", e);
    }
  };

  const btnPrimarySx = {
    py: 1.1,
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 2,
    bgcolor: ACCENT,
    color: "#fff",
    boxShadow: "none",
    "&:hover": { bgcolor: ACCENT_HOVER, boxShadow: "none" },
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 440 },
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          boxShadow: `-12px 0 40px ${alpha(theme.palette.common.black, 0.12)}`,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          background: `linear-gradient(125deg, ${alpha(ACCENT, 0.22)} 0%, ${alpha(
            theme.palette.background.paper,
            1,
          )} 55%, ${alpha(theme.palette.divider, 0.06)} 100%)`,
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                bgcolor: alpha(ACCENT, 0.35),
                color: theme.palette.text.primary,
              }}
            >
              <BusinessOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  color: "text.secondary",
                  lineHeight: 1.2,
                }}
              >
                CRM
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.3, mt: 0.25 }}>
                {isNew
                  ? t("ProveedoresPage.drawerNewSupplierTitle")
                  : t("ProveedoresPage.drawerEditSupplierTitle")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {t("ProveedoresPage.drawerSupplierSubtitle")}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t("ProveedoresPage.closePanel")}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              borderRadius: 2,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            borderColor: alpha(theme.palette.divider, 0.85),
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Stack spacing={2}>
            <TextField
              fullWidth
              label={t("ProveedoresPage.labelSupplierName")}
              name="N_PROVEEDOR"
              value={formData.N_PROVEEDOR || ""}
              onChange={handleChange}
              sx={fieldSx}
            />

            <FormControl fullWidth sx={fieldSx}>
              <InputLabel id="proveedor-tipo-label">
                {t("ProveedoresPage.labelSupplierTypeSelect")}
              </InputLabel>
              <Select
                labelId="proveedor-tipo-label"
                name="ProveedorTipo"
                value={formData.ProveedorTipo || ""}
                label={t("ProveedoresPage.labelSupplierTypeSelect")}
                onChange={handleChange}
              >
                {tiposProveedores.map((tipo) => (
                  <MenuItem key={tipo.id_ProveedorTipo} value={tipo.id_ProveedorTipo}>
                    {tipo.ProveedorTipo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={t("ProveedoresPage.labelStreetAddress")}
              name="DIRECCION"
              value={formData.DIRECCION || ""}
              onChange={handleChange}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              label={t("ProveedoresPage.labelStreetNumber")}
              name="NVIA"
              value={formData.NVIA || ""}
              onChange={handleChange}
              sx={fieldSx}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label={t("ProveedoresPage.labelPostalShort")}
                name="CP"
                value={formData.CP || ""}
                onChange={handleChange}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                label={t("ProveedoresPage.labelCityEn")}
                name="POBLACION"
                value={formData.POBLACION || ""}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Stack>

            <TextField
              fullWidth
              label={t("ProveedoresPage.labelProvinceEn")}
              name="PROVINCIA"
              value={formData.PROVINCIA || ""}
              onChange={handleChange}
              sx={fieldSx}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label={t("ProveedoresPage.labelCountryCodeEn")}
                name="CODPAIS"
                value={formData.CODPAIS || ""}
                onChange={handleChange}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                label={t("ProveedoresPage.labelCountryEn")}
                name="PAIS"
                value={formData.PAIS || ""}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label={t("ProveedoresPage.labelEmailField")}
                name="EMAIL"
                value={formData.EMAIL || ""}
                onChange={handleChange}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                label={t("ProveedoresPage.labelPhoneField")}
                name="TELEFONO"
                value={formData.TELEFONO || ""}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Stack>

            <TextField
              fullWidth
              label={t("ProveedoresPage.labelCurrency")}
              name="MONEDA"
              value={formData.MONEDA || ""}
              onChange={handleChange}
              sx={fieldSx}
            />
          </Stack>
        </Paper>
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          bgcolor: alpha(theme.palette.background.default, 0.65),
        }}
      >
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              flex: 1,
              py: 1.1,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              borderColor: alpha(theme.palette.text.primary, 0.2),
            }}
          >
            {t("ProveedoresPage.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSave} sx={{ flex: 1, ...btnPrimarySx }}>
            {t("ProveedoresPage.save")}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default ProveedoresFichaDrawer;
